"use client";

import { formatPhoneNumber } from "@/helpers/formatPhone";
import { getPhoneForLocation } from "@/lib/consts";
import { LocationEnum } from "@/lib/locationEnum";
import { useMessages } from "@/lib/i18n/useMessages";
import { cityNames, getCitySlug } from "@/lib/strings/expertiseCityStrings";
import { PhoneAndroidRounded } from "@mui/icons-material";
import { Button, Stack } from "@mui/material";
import { usePathname } from "next/navigation";
import { SLCopyToClipboardButton } from "../buttons/SLCopyToClipboardButton";
import { SLDialog } from "./SLDialog";

/**
 * Detects the location from the current URL path
 * Matches patterns like /expertise/{expertise-slug}/{city-slug}
 */
const useLocationFromPath = (): LocationEnum | undefined => {
  const pathname = usePathname();

  // Check each location's slug against the pathname
  for (const location of Object.values(LocationEnum)) {
    const citySlug = getCitySlug(location);
    if (pathname?.includes(`/${citySlug}`)) {
      return location;
    }
  }

  return undefined;
};

export const SLDialogAnrufen = ({
  handleClose,
  open,
  location: explicitLocation,
}: {
  handleClose: () => void;
  open: boolean;
  location?: LocationEnum;
}) => {
  const d = useMessages().dialogs;
  const detectedLocation = useLocationFromPath();
  const location = explicitLocation ?? detectedLocation;

  const phoneNumber = getPhoneForLocation(location);
  const formattedPhone = formatPhoneNumber(phoneNumber);

  const cityName = location ? cityNames[location] : undefined;
  const subtitle = cityName ? `${d.call.subtitle}` : d.call.subtitle;

  return (
    <SLDialog
      onClose={handleClose}
      open={open}
      title={d.call.title}
      subtitle={subtitle}
      content={
        <Stack direction="column" spacing={4} sx={{ mt: 4 }}>
          <SLCopyToClipboardButton
            text={phoneNumber}
            buttonText={formattedPhone}
            tooltipText={d.call.copy}
            successMessage={d.call.copySuccess}
            variant="contained"
            color="secondary"
            size="large"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          />
          <Button
            size="large"
            variant="contained"
            color="secondary"
            startIcon={<PhoneAndroidRounded fontSize="inherit" />}
            href={`tel:${phoneNumber}`}
          >
            {d.call.directCall}
          </Button>
        </Stack>
      }
    />
  );
};
